import { datadogRum } from "@datadog/browser-rum";
import { datadogLogs } from "@datadog/browser-logs";

import { logger } from "./browser-logger";

import type { UserInfo, ViewInfo, EventData } from "./datadog-types";

/**
 * Helper functions for consistent RUM log prefixes
 */
const logWarn = (message: string, data: Record<string, unknown>): void =>
  logger.warn(`RUM: ${message}`, data);
const logError = (message: string, data: Error): void =>
  logger.error(`RUM: ${message}`, data);

/**
 * Helper to check if RUM is initialized
 */
function isRumInitialized(): boolean {
  try {
    // Try to access a RUM property that would throw if not initialized
    return datadogRum.getInternalContext() !== undefined;
  } catch {
    return false;
  }
}

/**
 * Picks the role to report for a user.
 * If the user has an "admin" role, "admin" is used regardless of other roles.
 * Otherwise, the first role in the list is used.
 *
 * @param roles - List of role strings
 * @returns The selected role, or undefined if no roles are provided
 */
function pickUserRole(roles?: string[]): string | undefined {
  if (!roles || roles.length === 0) {
    return undefined;
  }
  if (roles.some((role) => role?.toLowerCase() === "admin")) {
    return "admin";
  }
  return roles[0];
}

/**
 * Sets user information in Datadog RUM and/or Logs (depending on what's initialized).
 * This associates all subsequent events with the specified user.
 *
 * @param name - User's full name
 * @param email - User's email address
 * @param userType - Formatted user type string
 * @param roles - Optional list of user roles; a single `role` is derived and
 *   included in the user data (admin wins if present, otherwise the first role).
 */
export function loginMetrics(
  name: string,
  email: string,
  userType: string,
  roles?: string[],
): void {
  const role = pickUserRole(roles);
  const userData = { id: email, name, email, userType, ...(role && { role }) };
  datadogRum.setUser(userData);
  datadogLogs.setUser(userData);
}

/**
 * Clears user information from Datadog RUM and/or Logs.
 * Call this when a user logs out.
 */
export function clearUser(): void {
  datadogRum.clearUser();
  datadogRum.clearAccount();
  datadogLogs.clearUser();
}

/**
 * Sets account information in Datadog RUM for account-level analytics.
 *
 * @param id - Account identifier (external broker ID or internal role name)
 * @param name - Account display name
 */
export function setAccount(id: string, name: string): void {
  datadogRum.setAccount({ id, name });
}

/**
 * Starts a new view in Datadog RUM.
 * Only works if RUM is initialized.
 *
 * @param options - View options
 */
export function addView(options: { name: string; service?: string }): void {
  if (isRumInitialized()) {
    datadogRum.startView(options);
  }
}

/**
 * Sets a global context property that will be included in all subsequent events.
 * The property is added to RUM (if initialized) and Logs.
 *
 * @param key - Property key
 * @param value - Property value
 */
export function setGlobalContext(key: string, value: unknown): void {
  datadogRum.setGlobalContextProperty(key, value);
  datadogLogs.setGlobalContextProperty(key, value);
}

/**
 * Tracks a user login event.
 * Sets the user context in Datadog and tracks a login action.
 *
 * @param userInfo - User information object
 * @example
 * ```typescript
 * loginEvent({
 *   name: 'John Doe',
 *   email: 'john@example.com',
 *   userType: 'Broker'
 * });
 * ```
 */
export function loginEvent(userInfo: UserInfo): void {
  try {
    // Set user context in Datadog
    loginMetrics(
      userInfo.name,
      userInfo.email,
      userInfo.userType,
      userInfo.roles,
    );
    // Track login action
    submitEvent("login", {
      name: userInfo.name,
      type: userInfo.userType,
      email: userInfo.email,
    });
  } catch (e) {
    logError("Datadog login event failed", e as Error);
  }
}

/**
 * Tracks a generic event with optional data.
 *
 * @param event - Event name string
 * @param data - Optional event data object
 * @example
 * ```typescript
 * submitEvent('button_clicked', { buttonId: 'submit-form' });
 * ```
 */
export function submitEvent(event: string, data?: EventData): void {
  if (typeof event === "string") {
    try {
      if (isRumInitialized()) {
        datadogRum.addAction(event, data);
      } else {
        // Fall back to logging the action when RUM is not initialized
        datadogLogs.logger.info(event, data);
      }
    } catch (e) {
      logError("Datadog submitEvent failed", e as Error);
    }
  } else {
    // Handle legacy event objects
    logWarn("Event object format is deprecated, use string event names", {
      event,
    });
  }
}

/**
 * Tracks a page view with title and path.
 * Creates a named view in Datadog RUM and tracks it as an action.
 *
 * @param viewInfo - View information object
 * @example
 * ```typescript
 * submitView({
 *   path: '/dashboard',
 *   title: 'Dashboard'
 * });
 * ```
 */
export function submitView(viewInfo: ViewInfo): void {
  try {
    const url = new URL(viewInfo.path, window.location.origin);
    // Use Datadog's native view tracking
    addView({ name: `${viewInfo.title} Viewed`, service: undefined });
    // Also track as an action for consistency
    submitEvent(`${viewInfo.title} Viewed`, {
      pageTitle: viewInfo.title,
      pagePath: url.pathname,
      pageUrl: `${url.origin}${url.pathname}`,
    });
  } catch (e) {
    logError("Datadog submitView failed", e as Error);
  }
}

/**
 * Tracks a user logout event and clears user context.
 * Call this when a user logs out of the application.
 */
export function logoutEvent(): void {
  try {
    submitEvent("logout");
    clearUser();
  } catch (e) {
    logError("Datadog logout event failed", e as Error);
  }
}
